import { useParams } from 'react-router-dom'
import { getRequest } from '../../../../services/base'
import Button from '../../../common/Button'
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { NavContext } from '../../../../context/global_context'
import { notifyError } from '../../../../utils/notifications'
import { downloadErrorLogs } from '../../../../utils/common'
import { useIntl } from 'react-intl'
import { Empty, Flex, Form, Popover, Table, Modal } from 'antd'
import { CloseOutlined, ExclamationCircleOutlined } from '@ant-design/icons'
import InputText from '../../../common/InputText'
import EllipsisTooltip from '../../../common/EllipsisTooltip'
import RecordsCount from '../../../common/RecordsCount'

const SELECT_ALL_KEY = '__select_all__'

const ExportMissingConnectors = () => {
  const intl = useIntl()
  const { vehicle } = useParams()
  const [state, dispatch] = useContext(NavContext)

  const exportMissingConnectorsRef = useRef()
  const [showCategory, setShowCategory] = useState(false)
  const [categoryData, setCategoryData] = useState([])
  const [filteredCategoryData, setFilteredCategoryData] = useState([])
  const [selectedCategory, setSelectedCategory] = useState([])
  const [searchValue, setSearchValue] = useState('')
  const [modal, contextHolder] = Modal.useModal()
  const [modalOpen, setModalOpen] = useState(false)

  const getCategoryList = useCallback(async () => {
    let res = await getRequest(`/api/connector/category/?vehicle=${vehicle}`)
    // base.js returns {status: false} instead of rejecting. Falling through put
    // `undefined` in state, which crashed on the next render at [...categoryData].
    if (res?.status === false) {
      notifyError(dispatch, intl.formatMessage({ id: 'functionalDiagram.categoriesLoadError' }))
      return
    }
    setCategoryData(res?.data || [])
    setFilteredCategoryData(res?.data || [])
  }, [vehicle])

  const searchCategory = useCallback(
    (value) => {
      let tempFilteredData = []
      if (value) {
        tempFilteredData = [...categoryData].filter((category) =>
          category.toLowerCase().includes(value.toLowerCase())
        )
      } else {
        tempFilteredData = [...categoryData]
      }
      setFilteredCategoryData(tempFilteredData)
    },
    [categoryData]
  )

  const downloadMissingConnectors = useCallback(() => {
    if (selectedCategory?.length > 0) {
      setModalOpen(true)

      modal.confirm({
        icon: <ExclamationCircleOutlined />,
        title: intl.formatMessage({
          id: 'functionalDiagram.downloadMissingConnectorsConfirmationTitle',
        }),
        content: intl.formatMessage({
          id: 'functionalDiagram.downloadMissingConnectorsConfirmationContent',
        }),
        centered: true,
        maskClosable: true,
        onOk: async () => {
          dispatch({
            type: 'loader',
            data: {
              caption: intl.formatMessage({ id: 'functionalDiagram.downloadingMissingConnectors', }),
              showLoader: true,
            },
            notification: {},
          })

          let res = await getRequest(
            `/api/sub_system/functional-diagram/missing/connectors/?vehicle=${vehicle}&category=${selectedCategory.join(',')}`
          )
          if (res?.status) {
            downloadErrorLogs(
              res?.data,
              `functional-diagram-connectors-${vehicle}.txt`
            )
            dispatch({
              type: 'notification',
              data: {
                notification_type: 'success',
                msg: intl.formatMessage({ id: 'functionalDiagram.downloadSuccess', }),
              },
            })
            setSelectedCategory([])
            setShowCategory(false)
          } else {
            dispatch({
              type: 'notification',
              data: {
                notification_type: 'error',
                msg: intl.formatMessage({ id: 'functionalDiagram.exportError' }),
              },
            })
          }
          setModalOpen(false)
        },
        onCancel: () => {
          setModalOpen(false)
        },
      })
    } else {
      dispatch({
        type: 'notification',
        data: {
          notification_type: 'error',
          msg: intl.formatMessage({ id: 'functionalDiagram.selectCategoryMessage', }),
        },
      })
    }
  }, [selectedCategory, vehicle, intl, dispatch, modal])

  const allVisibleSelected = useMemo(
    () =>
      filteredCategoryData?.length > 0 &&
      filteredCategoryData.every((category) =>
        selectedCategory.includes(category)
      ),
    [filteredCategoryData, selectedCategory]
  )

  const toggleSelectAll = useCallback(() => {
    if (allVisibleSelected) {
      setSelectedCategory((prev) =>
        prev.filter((category) => !filteredCategoryData.includes(category))
      )
    } else {
      setSelectedCategory((prev) => [
        ...new Set([...prev, ...filteredCategoryData]),
      ])
    }
  }, [allVisibleSelected, filteredCategoryData])

  const tableData = useMemo(
    () =>
      filteredCategoryData?.length
        ? [
            { category: SELECT_ALL_KEY },
            ...filteredCategoryData.map((category) => ({ category })),
          ]
        : [],
    [filteredCategoryData]
  )

  const columns = useMemo(
    () => [
      {
        title: '',
        render: (_, record) =>
          record.category === SELECT_ALL_KEY ? (
            <span className="bolder" data-testid="fd-category-select-all">{intl.formatMessage({ id: 'functionalDiagram.selectAll' })}</span>
          ) : (
            <EllipsisTooltip className="bolder ellipsis">
              {record.category}
            </EllipsisTooltip>
          ),
      },
    ],
    []
  )

  const rowSelection = useMemo(
    () => ({
      selectedRowKeys: allVisibleSelected
        ? [SELECT_ALL_KEY, ...selectedCategory]
        : selectedCategory,
      onChange: (newSelectedRowKeys) => {
        const willBeAllSelected = newSelectedRowKeys.includes(SELECT_ALL_KEY)
        if (willBeAllSelected !== allVisibleSelected) {
          toggleSelectAll()
          return
        }
        setSelectedCategory(
          newSelectedRowKeys.filter((key) => key !== SELECT_ALL_KEY)
        )
      },
    }),
    [allVisibleSelected, selectedCategory, toggleSelectAll]
  )

  const handleRowClick = useCallback(
    (record) => ({
      onClick: (e) => {
        if (e.target.closest('.ant-checkbox-wrapper')) {
          return
        }

        if (record.category === SELECT_ALL_KEY) {
          toggleSelectAll()
          return
        }

        setSelectedCategory((prev) =>
          prev.includes(record.category)
            ? prev.filter((key) => key !== record.category)
            : [...prev, record.category]
        )
      },
    }),
    [toggleSelectAll]
  )

  const handlePopoverOpenChange = useCallback(
    (open) => {
      if (modalOpen) return
      setShowCategory(open)
    },
    [modalOpen]
  )

  const handleClosePopover = useCallback(() => setShowCategory(false), [])

  const handleSearchChange = useCallback(
    (value) => {
      setSearchValue(value)
      searchCategory(value)
    },
    [searchCategory]
  )

  useEffect(() => {
    if (!showCategory) return

    getCategoryList()
  }, [showCategory, getCategoryList])

  return (
    <div ref={exportMissingConnectorsRef}>
      <Popover
        open={showCategory}
        onOpenChange={handlePopoverOpenChange}
        trigger="click"
        placement="bottomRight"
        classNames={{
          body: 'export-missing-connectors-popover',
        }}
        title={
          <Flex justify="space-between" align="center">
            {intl.formatMessage({
              id: 'functionalDiagram.selectCategoriesToDownload',
            })}
            <Button
              type="text"
              icon={<CloseOutlined />}
              className="ant-popover-close"
              onClick={handleClosePopover}
              aria-label="Close"
            />
          </Flex>
        }
        content={
          <Flex vertical gap={16} style={{ width: 600 }}>
            <Form>
              <InputText
                name="category"
                placeholder={intl.formatMessage({
                  id: 'functionalDiagram.searchCategoriesPlaceholder',
                })}
                type="search"
                isRequired={false}
                onChange={handleSearchChange}
                value={searchValue}
              />
            </Form>
            <div className="categories-list">
              <Table
                rowKey="category"
                columns={columns}
                dataSource={tableData}
                tableLayout="fixed"
                scroll={{ y: '60vh' }}
                showHeader={false}
                pagination={false}
                rowSelection={rowSelection}
                onRow={handleRowClick}
                locale={{
                  emptyText: (
                    <div style={{ padding: '40px' }}>
                      <Empty description={<RecordsCount count={0} />} />
                    </div>
                  ),
                }}
              />
            </div>
            <Button
              type="primary"
              style={{ alignSelf: 'flex-end' }}
              disabled={selectedCategory.length === 0 ? true : false}
              onClick={downloadMissingConnectors}
              testId="fd-download-missing-submit"
            >
              {intl.formatMessage({ id: 'forms.download' })}
            </Button>
          </Flex>
        }
      >
        <Button type="primary" testId="fd-download-missing">
          {intl.formatMessage({
            id: 'functionalDiagram.downloadMissingConnectors',
          })}
        </Button>
      </Popover>

      {contextHolder}
    </div>
  )
}

export default ExportMissingConnectors
